import { useAuthenticator } from '@aws-amplify/ui-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { clearAllApplicationState } from '@/utils/storeUtils';

/**
 * Custom hook that provides centralized authentication logic
 */
export const useAuth = () => {
  const { signOut, user, authStatus } = useAuthenticator();
  const [isAdmin, setIsAdmin] = useState(false);

  const isAuthenticated = authStatus === 'authenticated';
  const isLoading = authStatus === 'configuring';

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isAuthenticated && user) {
        try {
          const userAttributes = await fetchUserAttributes();
          // Check for admin role in user attributes or groups
          // This can be customized based on how admin status is stored
          const userGroups = userAttributes['custom:groups'] || '';
          setIsAdmin(userGroups.includes('admin'));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching user attributes:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, user]);

  const handleSignOut = useCallback(() => {
    try {
      // Clear all in-memory application state before signing out
      // This ensures that when a different user signs in using the same browser window,
      // they get their own fresh data and settings
      clearAllApplicationState();
      signOut();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error signing out:', error);
    }
  }, [signOut]);

  return {
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    signOut: handleSignOut
  };
};
